import React, { useEffect, useMemo, useState } from 'react';
import { useSchedulingClient } from './scheduling.client';
import { Calendar, Views, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import 'react-big-calendar/lib/css/react-big-calendar.css';
export const MeetingsPage = () => {
    const { getEvents, deleteEvent, getCalendars } = useSchedulingClient();
    const [loading, setLoading] = useState(true);
    const [events, setEvents] = useState([]);
    const [error, setError] = useState();
    const [calendars, setCalendars] = useState([]);
    const [calendarId, setCalendarId] = useState();
    const [view, setView] = useState(Views.WEEK);
    const [date, setDate] = useState(new Date());
    const now = useMemo(() => new Date(), []);
    const startsAfterIso = useMemo(() => new Date(now.getTime() - 7 * 24 * 3600 * 1000).toISOString(), [now]);
    const endsBeforeIso = useMemo(() => new Date(now.getTime() + 30 * 24 * 3600 * 1000).toISOString(), [now]);
    const load = async () => {
        try {
            setLoading(true);
            setError(undefined);
            const resp = await getEvents({ startsAfter: startsAfterIso, endsBefore: endsBeforeIso, limit: 100, calendarId });
            setEvents(resp.events || []);
        }
        catch (e) {
            setError('Failed to load events');
        }
        finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        (async () => {
            try {
                const c = await getCalendars();
                setCalendars(c.calendars || []);
                if (!calendarId) {
                    const pick = (c.calendars || []).find((x) => x?.is_primary || x?.read_only === false) || (c.calendars || [])[0];
                    if (pick?.id)
                        setCalendarId(pick.id);
                }
            }
            catch (_) {
                setError('Failed to load calendars');
            }
            await load();
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [calendarId]);
    const handleDelete = async (eventId) => {
        if (!eventId)
            return;
        if (!confirm('Delete this event?'))
            return;
        try {
            await deleteEvent(eventId);
            await load();
        }
        catch (e) {
            alert('Failed to delete event');
        }
    };
    const locales = {};
    const localizer = dateFnsLocalizer({
        format,
        parse,
        startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 0 }),
        getDay,
        locales,
    });
    const calendarEvents = useMemo(() => {
        return events
            .map((ev) => {
            const id = ev?.id || ev?._id;
            const startSec = ev?.when?.start_time ?? ev?.when?.start ?? undefined;
            const endSec = ev?.when?.end_time ?? ev?.when?.end ?? undefined;
            const start = typeof startSec === 'number' ? new Date(startSec * 1000) : startSec ? new Date(startSec) : undefined;
            const end = typeof endSec === 'number' ? new Date(endSec * 1000) : endSec ? new Date(endSec) : undefined;
            if (!id || !start || !end)
                return undefined;
            return {
                id,
                title: ev?.title || 'Untitled event',
                start,
                end,
            };
        })
            .filter(Boolean);
    }, [events]);
    return (React.createElement("div", { className: "p-4" },
        React.createElement("div", { className: "flex items-center justify-between mb-3" },
            React.createElement("div", null,
                React.createElement("div", { className: "text-xl font-semibold text-gray-800" }, "Meetings"),
                React.createElement("div", { className: "text-sm text-gray-500" }, "List and delete meetings created via Nylas")),
            React.createElement("div", { className: "flex items-center gap-2" },
                React.createElement("select", { className: "px-2 py-1 border border-gray-300 rounded-md text-sm bg-white", value: calendarId || '', onChange: (e) => setCalendarId(e.target.value) },
                    React.createElement("option", { value: "", disabled: true }, "Select calendar..."),
                    calendars.map((c) => (React.createElement("option", { key: c.id, value: c.id }, c.name || c.email || c.id)))),
                React.createElement("button", { className: "px-2.5 py-1.5 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm", onClick: load }, "Refresh"))),
        error && React.createElement("div", { className: "text-sm text-red-600 mb-2" }, error),
        React.createElement("div", { className: "h-[70vh] border border-gray-200 rounded-lg bg-white" },
            React.createElement(Calendar, { localizer: localizer, events: calendarEvents, startAccessor: "start", endAccessor: "end", titleAccessor: "title", views: [Views.DAY, Views.WEEK, Views.MONTH, Views.AGENDA], view: view, onView: (v) => setView(v), date: date, onNavigate: (d) => setDate(d), toolbar: true, popup: true, components: {
                    toolbar: (props) => (React.createElement("div", { className: "flex items-center justify-between px-3 py-2 border-b border-gray-200" },
                        React.createElement("div", { className: "flex items-center gap-2" },
                            React.createElement("button", { className: "px-2 py-1 text-sm border border-gray-300 rounded-md", onClick: () => props.onNavigate('TODAY') }, "Today"),
                            React.createElement("button", { className: "px-2 py-1 text-sm border border-gray-300 rounded-md", onClick: () => props.onNavigate('PREV') }, "Prev"),
                            React.createElement("button", { className: "px-2 py-1 text-sm border border-gray-300 rounded-md", onClick: () => props.onNavigate('NEXT') }, "Next")),
                        React.createElement("div", { className: "text-sm font-medium text-gray-700" }, format(props.date, 'PPP')),
                        React.createElement("div", { className: "flex items-center gap-1" }, ['day', 'week', 'month', 'agenda'].map(v => (React.createElement("button", { key: v, className: `px-2 py-1 text-sm border rounded-md ${props.view === v ? 'bg-gray-100 border-gray-400' : 'border-gray-300'}`, onClick: () => props.onView(v) }, v.charAt(0).toUpperCase() + v.slice(1)))))))
                } }))));
};
export default MeetingsPage;
